<template>
  <!-- global AI Usage Request modal. mounted once in App.vue so any component can open it
       via $root.$emit('open-ai-usage-request-modal') without forcing navigation. -->
  <b-modal
    v-model="show"
    title="Request Additional AI Usage"
    size="md"
    :ok-title="submitting ? 'Sending...' : 'Send Request'"
    :ok-disabled="submitting || !reason.trim()"
    :cancel-disabled="submitting"
    @ok.prevent="submit"
    @hidden="onClosed"
  >
    <p style="font-size: 13px; color: #666; margin-bottom: 12px;">
      Your organization has hit its monthly AI usage limit. Tell us a bit about why you need more so we can get back to you quickly.
    </p>
    <div class="mb-3">
      <label style="font-size: 13px; font-weight: 600;">What would you like to do that needs more AI usage?</label>
      <b-form-textarea
        class="ai-usage-reason"
        v-model="reason"
        rows="6"
        max-rows="10"
        placeholder="e.g. Uploading a new driver handbook for the team to reference, or running a heavier chat workload this month..."
        :disabled="submitting"
      />
    </div>
    <p style="font-size: 12px; color: #888;">
      We'll open a support ticket and reach out. Existing documents stay available for download while you wait.
    </p>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import { create_support_ticket } from '../networking/apis'

export default {
  name: 'ai_usage_request_modal',
  data() {
    return {
      show: false,
      reason: '',
      submitting: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
  },
  mounted() {
    this._openHandler = () => {
      // close the Fleet Assistant panel first (if it's open). the FA panel
      // uses z-index 2147483001 to stay above every other app surface,
      // which on mobile means it covers this modal. rather than fight a
      // z-index war, just dismiss the panel — the usage-request flow is
      // a support-ticket submission that stands on its own and doesn't
      // need FA visible underneath. launcher listens for the event and
      // calls its own closePanel() if currently open.
      this.$root.$emit('close-fleet-assistant')
      this.reason = ''
      this.submitting = false
      this.show = true
    }
    this.$root.$on('open-ai-usage-request-modal', this._openHandler)
  },
  beforeDestroy() {
    if (this._openHandler) {
      this.$root.$off('open-ai-usage-request-modal', this._openHandler)
      this._openHandler = null
    }
  },
  methods: {
    onClosed() {
      this.reason = ''
      this.submitting = false
    },
    async submit() {
      const reason = (this.reason || '').trim()
      if (!reason) return
      this.submitting = true
      try {
        const description =
          'The user has requested additional AI usage after hitting the monthly cost limit.\n\n' +
          'Reason provided:\n' + reason
        await create_support_ticket(this.user.tenant, {
          category: 'fleet-assistant',
          priority: 'question',
          subject: 'Additional AI usage requested',
          description,
        })
        this.show = false
        this.$emit('showAlert', 'Support ticket created — we\'ll be in touch.', 'success', 4000)
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.error
          ? e.response.data.error
          : 'Could not send the request. Please try again or open a support ticket manually.'
        this.$emit('showAlert', msg, 'danger', 6000)
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style>
/* non-scoped: BootstrapVue's b-form-textarea renders the <textarea> without
   propagating vue's scoped data-v attribute, so scoped selectors on the
   textarea itself don't match. keeping the class prefix (.ai-usage-reason)
   unique avoids bleeding into other components. */

/* lighter placeholder so the example reads as hint text rather than a
   filled-in value — bootstrap's default is close to real-input color on
   iOS Safari. */
.ai-usage-reason::placeholder {
  color: #9ca3af;
  opacity: 1;
}
/* b-form-textarea auto-sizes with empty content to its minimum (~2 rows)
   even when rows="6" is set, which clips the placeholder on narrow
   viewports where it wraps to 5+ lines. force a floor so the full
   example is visible without the user having to click in. */
.ai-usage-reason {
  min-height: 160px;
}
</style>
